import { useRef, useMemo, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Points, PointMaterial } from '@react-three/drei';
import * as THREE from 'three';

function NetworkTopology({ count = 120 }) {
  const pointsRef = useRef<THREE.Points>(null);
  const linesRef = useRef<THREE.LineSegments>(null);
  const mousePosition = useRef({ x: 0, y: 0 });

  // Generate particles
  const particles = useMemo(() => {
    const coords = new Float32Array(count * 3);
    const velocities = [];
    for (let i = 0; i < count; i++) {
      // spread particles in a 3D box
      coords[i * 3] = THREE.MathUtils.randFloatSpread(20);
      coords[i * 3 + 1] = THREE.MathUtils.randFloatSpread(20);
      coords[i * 3 + 2] = THREE.MathUtils.randFloatSpread(20);

      velocities.push({
        x: THREE.MathUtils.randFloatSpread(0.015),
        y: THREE.MathUtils.randFloatSpread(0.015),
        z: THREE.MathUtils.randFloatSpread(0.015)
      });
    }
    return { coords, velocities };
  }, [count]);

  const maxConnections = count * count;
  const linePositions = useMemo(
    () => new Float32Array(maxConnections * 3),
    [maxConnections]
  );

  // Interaction
  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      mousePosition.current = {
        x: (event.clientX / window.innerWidth) * 2 - 1,
        y: -(event.clientY / window.innerHeight) * 2 + 1
      };
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useFrame(() => {
    if (!pointsRef.current || !linesRef.current) return;
    const positions = pointsRef.current.geometry.attributes.position
      .array as Float32Array;

    // Update positions based on velocity
    for (let i = 0; i < count; i++) {
      positions[i * 3] += particles.velocities[i].x;
      positions[i * 3 + 1] += particles.velocities[i].y;
      positions[i * 3 + 2] += particles.velocities[i].z;

      // Bounce off walls
      if (Math.abs(positions[i * 3]) > 10) particles.velocities[i].x *= -1;
      if (Math.abs(positions[i * 3 + 1]) > 10) particles.velocities[i].y *= -1;
      if (Math.abs(positions[i * 3 + 2]) > 10) particles.velocities[i].z *= -1;
    }
    pointsRef.current.geometry.attributes.position.needsUpdate = true;

    // Parallax mouse effect
    pointsRef.current.rotation.x +=
      (mousePosition.current.y * 0.1 - pointsRef.current.rotation.x) * 0.05;
    pointsRef.current.rotation.y +=
      (mousePosition.current.x * 0.1 - pointsRef.current.rotation.y) * 0.05;
    pointsRef.current.rotation.z += 0.001; // subtle auto-rotation
    
    linesRef.current.rotation.copy(pointsRef.current.rotation);

    // Connect points
    let vertexPos = 0;
    let numConnected = 0;
    for (let i = 0; i < count; i++) {
      for (let j = i + 1; j < count; j++) {
        const dx = positions[i * 3] - positions[j * 3];
        const dy = positions[i * 3 + 1] - positions[j * 3 + 1];
        const dz = positions[i * 3 + 2] - positions[j * 3 + 2];
        const distSq = dx * dx + dy * dy + dz * dz;

        // threshold for line connection
        if (distSq < 15) {
          linePositions[vertexPos++] = positions[i * 3];
          linePositions[vertexPos++] = positions[i * 3 + 1];
          linePositions[vertexPos++] = positions[i * 3 + 2];
          linePositions[vertexPos++] = positions[j * 3];
          linePositions[vertexPos++] = positions[j * 3 + 1];
          linePositions[vertexPos++] = positions[j * 3 + 2];
          numConnected++;
        }
      }
    }
    linesRef.current.geometry.setDrawRange(0, numConnected * 2);
    linesRef.current.geometry.attributes.position.needsUpdate = true;
  });

  return (
    <group>
      <Points ref={pointsRef}>
        <bufferGeometry>
          <bufferAttribute
            attach='attributes-position'
            count={count}
            array={particles.coords}
            itemSize={3}
          />
        </bufferGeometry>
        <PointMaterial
          transparent
          color='#00ffd1'
          size={0.06}
          sizeAttenuation={true}
          depthWrite={false}
          opacity={0.9}
          blending={THREE.AdditiveBlending}
        />
      </Points>
      <lineSegments ref={linesRef}>
        <bufferGeometry>
          <bufferAttribute
            attach='attributes-position'
            count={maxConnections}
            array={linePositions}
            itemSize={3}
          />
        </bufferGeometry>
        <lineBasicMaterial
          color='#00ffd1'
          transparent
          opacity={0.12}
          blending={THREE.AdditiveBlending}
        />
      </lineSegments>
    </group>
  );
}

const TechBackground3D = () => {
  return (
    <div className='fixed inset-0 z-0 pointer-events-none'>
        <div className="absolute inset-0 bg-background/80" />
      <Canvas
        camera={{ position: [0, 0, 10], fov: 60 }}
        style={{ background: 'transparent' }}
        gl={{ antialias: true, alpha: true }}
      >
        <fog attach='fog' args={['hsl(222, 47%, 4%)', 5, 20]} />
        <ambientLight intensity={0.5} />
        <NetworkTopology count={150} />
      </Canvas>
    </div>
  );
};

export default TechBackground3D;
